<template>
  <transition
    name="notification__transition"
    mode="out-in"
    appear
  >
    <div class="notification">
      <div class="notification__header">
        <h5>{{ mode === 'create' ? 'New notification' : 'Edit notification' }}</h5>

        <div class="notification__close">
          <UtilityIconButton
            size="md"
            variant="round"
            backgroundColor="#12130f"
            highlightColor="rgba(255,255,255,0.7)"
            highlightBackgroundColor="#070806"
            content="Close"
            v-tippy="{ theme: 'statzon', placement: 'left', arrow: true, animation: 'scale' }"
            @click.native="close()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M23 20.168l-8.185-8.187 8.185-8.174-2.832-2.807-8.182 8.179-8.176-8.179-2.81 2.81 8.186 8.196-8.186 8.184 2.81 2.81 8.203-8.192 8.18 8.192z"/></svg>
          </UtilityIconButton>
        </div>
      </div>

      <div class="notification__top">
        <div>
          <transition name="transition--slideDown">
            <span :key="'totalNotifications__' + totalNotifications" :class="[totalNotifications >= 40 && totalNotifications < 50 ? 'notification__top--warning' : '', totalNotifications >= 50 ? 'notification__top--danger' : '']">{{ totalNotifications }}</span>
          </transition>/50
          <span class="notification__top--light">notifications</span>
        </div>

        <div v-if="mode === 'create'">
          <router-link to="/settings/notifications" class="link" @click.native="close()">View all</router-link>
        </div>
      </div>

      <div
        v-if="!errorMessage && (mode === 'create' && totalNotifications < 50) || mode === 'edit'"
        class="notification__info"
      >
        <p v-if="mode === 'create' && totalNotifications < 50">Create a new notification to receive <span class="highlight--underline">email</span> whenever new data (based on your <span class="highlight--underline">selection of filters</span>) is published.</p>
        <p v-if="mode === 'edit'">You can edit name and sending frequency from this view. To edit filters, simply create new notification.</p>
      </div>

      <div class="notification__form">
        <div
          v-if="errorMessage || (mode === 'create' && totalNotifications >= 50)"
          class="row row--border"
        >
          <div class="row__full row__center">
            <p
              v-if="errorMessage"
              class="row__error"
              v-html="errorMessage"
            />
            <p
              v-else
              class="row__error"
            >
              You have reached the maximum limit of 50 active notifications. Consider deleting some old notifications to free active slots.
            </p>
          </div>
        </div>

        <div :class="['row', errorMessage || (mode === 'create' && totalNotifications >= 50) ? 'row--separator' : '']">
          <div class="row__full">
            <FormInput
              :value="name"
              type="text"
              refName="name"
              placeholder="Name it descriptively"
              label="Notification name"
              alt-label="Notification name"
              size="md"
              variant="dark"
              iconSize="md"
              :icon="icon.name"
              :error="errors.name"
              :status="status.name"
              @inputChanged="setFieldValue($event, 'name')"
              @unfocused="validate('name')"
            />
          </div>
        </div>

        <div class="row row--border">
          <div class="row__full">
            <FormSelect
              :value="frequency"
              :options="[{ 'key': 'daily', 'name': 'Daily' }, { 'key': 'weekly', 'name': 'Weekly' }, { 'key': 'monthly', 'name': 'Monthly' }]"
              type="select"
              size="md"
              variant="dark"
              ref-name="notificationFrequency"
              :placeholder="frequency.value"
              :prefilled="true"
              label="How often would you like to receive email?"
              altLabel="How often would you like to receive email?"
              :scrollPosition="0"
              iconSize="md"
              :icon="icon.frequency"
              :error="errors.frequency"
              :status="status.frequency"
              @valueChanged="setFieldValue($event, 'frequency')"
              @unfocused="validate('frequency')"
            />
          </div>
        </div>

        <div class="row row--border row--separator">
          <div class="row__full row__full--condensed">
            <FormCheckbox
              :value="mode === 'edit' ? dataObject.attributes.type === 'datasets' ? true : false : types['datasets']"
              :disabled="mode === 'edit' ? true : false"
              key="datasets"
              label="Datasets"
              @valueChanged="setTypesValue($event, 'datasets')"
            />

            <FormCheckbox
              :value="mode === 'edit' ? dataObject.attributes.type === 'pdfs' ? true : false : types['pdfs']"
              :disabled="mode === 'edit' ? true : false"
              key="pdfs"
              label="Reports"
              @valueChanged="setTypesValue($event, 'pdfs')"
            />

            <FormCheckbox
              :value="mode === 'edit' ? dataObject.attributes.type === 'news' ? true : false : types['news']"
              :disabled="mode === 'edit' ? true : false"
              key="news"
              label="News"
              @valueChanged="setTypesValue($event, 'news')"
            />
          </div>
        </div>

        <div class="row row--border row--separator">
          <div class="row__full row__full--filters">
            <template v-for="item in queryItems">
              <template v-if="!item.attributes.initializing || item.attributes.initializing === false">
                <div
                  v-if="item.type === 'operator'"
                  :key="'OPERATOR_' + item.id"
                  class="row__operator"
                >
                  <p>{{ item.attributes.name }}</p> <!-- + / @ small viewports -->
                </div>

                <UtilityFilter
                  v-else
                  :ref="'querySliderItem--' + item.id"
                  :key="'FILTER_' + item.type + '_' + item.id"
                  class="row__filter"
                  :value="item"
                  :noRemoval="true"
                />
              </template>
            </template>
          </div>
        </div>

        <div class="row row--separator">
          <div :class="['row__full', mode === 'create' ? 'row__center' : '']">
            <FormSubmit
              v-if="mode === 'edit'"
              :status="deleteStatus"
              variant="delete"
              :noIcon="true"
              @click="deleteNotification()"
            >
              {{ deleteStatus === 'success' ? 'Success' : 'Delete' }}
            </FormSubmit>

            <FormSubmit
              :status="formStatus"
              :disabled="mode === 'create' && totalNotifications >= 50 ? true : false"
              variant="blue"
              :icon="icon[mode]"
              @click="submitNotification()"
            >
              {{ formStatus === 'success' ? 'Success' : (mode === 'create' ? (totalNotifications >= 50 ? 'Limit reached' : 'Create notification') : 'Update') }}
            </FormSubmit>
          </div>
        </div>

        <div class="row">
          <div
            v-if="mode === 'create'"
            class="row__full row__notice noOverflow"
          >
            Email will be sent to <span class="highlight--underline">{{ userData.email }}</span><br />{{ frequency.key }} @ {{ notificationDeliveryTime }} UTC
            <router-link to="settings/personalization" class="link--plain" @click.native="close()">
              <span
                v-tippy="{ theme: 'statzon', placement: 'top', arrow: true, animation: 'scale' }"
                content="You can change <u>delivery time</u> in settings"
                class="notification__tooltip"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm1.25 17c0 .69-.559 1.25-1.25 1.25-.689 0-1.25-.56-1.25-1.25s.561-1.25 1.25-1.25c.691 0 1.25.56 1.25 1.25zm1.393-9.998c-.608-.616-1.515-.955-2.551-.955-2.18 0-3.59 1.55-3.59 3.95h2.011c0-1.486.829-2.013 1.538-2.013.634 0 1.307.421 1.364 1.226.062.847-.39 1.277-.962 1.821-1.412 1.343-1.438 1.993-1.432 3.468h2.005c-.013-.664.03-1.203.935-2.178.677-.73 1.519-1.638 1.536-3.022.011-.924-.284-1.719-.854-2.297z"/></svg>
              </span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import FormInput from '@/components/form/Input.vue'
import FormSelect from '@/components/form/Select.vue'
import FormButton from '@/components/form/Button.vue'
import FormCheckbox from '@/components/form/Checkbox.vue'
import FormSubmit from '@/components/form/Submit.vue'
import UtilityFilter from '@/components/utility/Filter.vue'
import UtilityIconButton from '@/components/utility/IconButton.vue'
import config from '@/helpers/config'
import helperMethods from '@/helpers/utility'

export default {
  components: {
    FormInput,
    FormSelect,
    FormButton,
    FormCheckbox,
    FormSubmit,
    UtilityFilter,
    UtilityIconButton
  },
  props: {
    mode: {
      type: String,
      default: 'create',
      validator (values) {
        return ['create', 'edit'].includes(values)
      }
    },
    dataObject: {
      type: [Object, Boolean],
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      form: false,
      formStatus: 'default',
      deleteStatus: 'default',
      notificationDeliveryTime: '??',
      name: this.dataObject && this.dataObject.attributes.name ? this.dataObject.attributes.name : this.generateName(),
      frequency: this.dataObject && this.dataObject.attributes.frequency ? { 'key': this.dataObject.attributes.frequency, 'value': this.dataObject.attributes.frequency.charAt(0).toUpperCase() + this.dataObject.attributes.frequency.slice(1) } : { 'key': 'weekly', 'value': 'Weekly' },
      types: {
        'datasets': ['Search', 'Datasets', 'Dataset'].includes(this.$route.name) ? true : false,
        'pdfs': ['Search', 'PDFs', 'PDF'].includes(this.$route.name) ? true : false,
        'news': ['Search', 'News'].includes(this.$route.name) ? true : false
      },
      errorMessage: '',
      errors: {
        name: false,
        frequency: false
      },
      status: {
        name: false,
        frequency: false
      },
      icon: {
        name: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-.001 5.75c.69 0 1.251.56 1.251 1.25s-.561 1.25-1.251 1.25-1.249-.56-1.249-1.25.559-1.25 1.249-1.25zm2.001 12.25h-4v-1c.484-.179 1-.201 1-.735v-4.467c0-.534-.516-.618-1-.797v-1h3v6.265c0 .535.517.558 1 .735v.999z"/></svg>',
        frequency: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M17 3v-2c0-.552.447-1 1-1s1 .448 1 1v2c0 .552-.447 1-1 1s-1-.448-1-1zm-12 1c.553 0 1-.448 1-1v-2c0-.552-.447-1-1-1-.553 0-1 .448-1 1v2c0 .552.447 1 1 1zm13 13v-3h-1v4h3v-1h-2zm-5 .5c0 2.481 2.019 4.5 4.5 4.5s4.5-2.019 4.5-4.5-2.019-4.5-4.5-4.5-4.5 2.019-4.5 4.5zm11 0c0 3.59-2.91 6.5-6.5 6.5s-6.5-2.91-6.5-6.5 2.91-6.5 6.5-6.5 6.5 2.91 6.5 6.5zm-14.237 3.5h-7.763v-13h19v1.763c.727.33 1.399.757 2 1.268v-9.031h-3v1c0 1.316-1.278 2.339-2.658 1.894-.831-.268-1.342-1.111-1.342-1.984v-.91h-9v1c0 1.316-1.278 2.339-2.658 1.894-.831-.268-1.342-1.111-1.342-1.984v-.91h-3v21h11.031c-.511-.601-.938-1.273-1.268-2z"/></svg>',
        create: '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M13 18v-4h-7v-4h7v-4l6 6-6 6zm-1-16c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12z"/></svg>',
        edit: '<svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill-rule="evenodd" clip-rule="evenodd"><path d="M8 6.012h-6.58l1.935-6.012 1.718 2.223c1.958-1.389 4.346-2.211 6.927-2.211 6.623 0 12 5.377 12 12s-5.377 11.988-12 11.988-12-5.365-12-11.988c0-1.036.132-2.041.379-3h2.079c-.297.947-.458 1.955-.458 3 0 5.52 4.481 10 10 10 5.52 0 10-4.48 10-10 0-5.519-4.48-10-10-10-2.121 0-4.083.668-5.703 1.796l1.703 2.204zm4 1.988c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4z"/></svg>'
      }
    }
  },
  computed: {
    isAuthenticated () {
      return this.$store.getters['user/isAuthenticated']
    },
    settings () {
      return this.$store.getters['app/getSettings']
    },
    userData () {
      return this.$store.getters['user/getData']
    },
    notifications () {
      return this.$store.getters['user/getNotifications']
    },
    totalNotifications () {
      if (this.notifications && this.notifications.byId) {
        return Object.keys(this.notifications.byId).length
      }

      return 0
    },
    selectedFilters () {
      return this.$store.getters['search/getSelectedFilters']
    },
    queryItems () {
      let items = []
      let i = 0
      let initialized = false
      let previousType = false
      let operatorName = false
      let filters = this.mode === 'create' ? this.selectedFilters : this.dataObject.attributes.filters

      for (const [groupKey, group] of Object.entries(filters)) {
        if (group.length > 0) {
          for (const [key, filter] of Object.entries(group)) {
            if (filter.id && (!filter.attributes.initializing || filter.attributes.initializing === false)) {
              operatorName = previousType === filter.type ? 'OR' : 'AND'
              let operator = { 'id': 'OPERATOR_' + filter.type + filter.id + '_' + i, 'type': 'operator', 'attributes': { 'name': operatorName, 'condition': '' }}

              if (filter.type !== 'published') {
                items.push(operator)
                items.push(filter)
              }
            } else {
              initialized = true
            }

            previousType = groupKey
          }
        }

        i++
      }

      if (initialized === false) {
        this.initializing = false
      }

      items.shift()
      return items
    }
  },
  mounted () {
    this.getNotificationDeliveryTime()
  },
  methods: {
    async submitNotification () {
      this.errorMessage = ''

      if (this.validate('all')) {
        this.formStatus = 'loading'

        let formData = this.mode === 'create' ? {
          name: this.name,
          types: this.formatTypes(),
          frequency: this.frequency.key,
          filters: this.$store.getters['search/getFiltersQuery'](this.type, 'client').slice(0, -1)
        } : {
          id: this.dataObject.id,
          name: this.name,
          frequency: this.frequency.key,
          filters: this.dataObject.attributes.query
        }

        try {
          let response = this.mode === 'create' ? await this.$store.dispatch('user/addNotification', formData) : await this.$store.dispatch('user/editNotification', formData)

          if (this.mode === 'create' && response && response.data) {
            let results = this.notifications.byId ? this.notifications.byId : {}
            let resultIds = this.notifications.allIds ? this.notifications.allIds : []

            response.data.forEach(result => {
              if (result.type === 'notifications') {
                let resultId = 'NOTIFICATION_' + result.id

                results[resultId] = result
                resultIds.push(resultId)
              }
            })

            let normalizedResults = {
              'byId': results,
              'allIds': resultIds
            }

            this.$store.dispatch('user/setNotifications', normalizedResults)
          }

          this.formStatus = 'success'
          this.mode === 'create' ? this.sendGoogleAnalyticsEvent('notification_create', this.formatTypes(), this.$route.name, this.$store.getters['user/getSubscriptionStatus']) : this.sendGoogleAnalyticsEvent('notification_update', this.dataObject.attributes.type, this.$route.name, this.$store.getters['user/getSubscriptionStatus'])
          setTimeout(() => this.$emit('submitted', true), 1250)
        } catch (error) {
          if (error.data) {
            if (error.data.errors && Array.isArray(error.data.errors)) {
              error.data.errors.forEach(err => {
                this.errors[err.source.pointer] = err.title
                this.errorMessage += err.title + '<br>'
              })
            } else if (error.data.errors) {
              this.errorMessage = error.data.errors.title
            }
          }

          this.formStatus = 'error'
        }
      }
    },
    async deleteNotification () {
      let notificationId = this.dataObject && this.dataObject.id ? this.dataObject.id : false

      if (notificationId !== false) {
        let type = this.dataObject.attributes.type
        this.deleteStatus = 'loading'
        await this.$store.dispatch('user/deleteNotification', notificationId)
        this.deleteStatus = 'success'
        this.sendGoogleAnalyticsEvent('notification_delete', type, this.$route.name, this.$store.getters['user/getSubscriptionStatus'])
        setTimeout(() => this.$emit('deleted', notificationId), 1250)
      }
    },
    getNotificationDeliveryTime () {
      if (config && this.settings && this.settings.user.NotificationDeliveryTime) {
        config.settings.notificationDeliveryTimeOptions.forEach(check => {
          if (check.key === this.settings.user.NotificationDeliveryTime) {
            this.notificationDeliveryTime = check.name
          }
        })
      }
    },
    setFieldValue (value, field) {
      this[field] = value
    },
    setTypesValue (value, field) {
      this.types[field] = value
    },
    formatTypes () {
      let types = []

      for (const [key, value] of Object.entries(this.types)) {
        if (value === true) {
          types.push(key)
        }
      }

      return types.join(',')
    },
    validate (field) {
      let valid = true

      if (field === 'name' || field === 'all') {
        if (this.name === '') {
          this.errors.name = 'Required field'
          valid = false
        } else if (this.name && this.name.length < 2) {
          this.errors.name = 'Minimum of 2 characters'
          valid = false
        } else if (this.name && this.name.length > 128) {
          this.errors.name = 'Maximum of 128 characters'
          valid = false
        } else {
          this.errors.name = undefined
        }

        this.status.name = this.errors.name ? 'invalid' : 'valid'
      }

      if (field === 'frequency' || field === 'all') {
        if (this.frequency.key === '' || this.frequency.key === undefined) {
          this.errors.frequency = 'Required field'
          valid = false
        } else {
          this.errors.frequency = undefined
        }

        this.status.frequency = this.errors.frequency ? 'invalid' : 'valid'
      }

      // Filters (custom validation)...

      return valid
    },
    isFilterDisabled (value) {
      let view = this.getSearchView()

      if (value.type === 'queries') {
        return false
      }

      if (config.filterFields[view]) {
        for (const filter of config.filterFields[view]) {
          if (value.type === filter.key) {
            return false
          }
        }
      }

      return true
    },
    generateName () {
      let filters = this.$store.getters['search/getSelectedFilters']
      let parts = []
      let name = ''

      if (this.mode === 'create' && filters) {
        for (const [groupKey, group] of Object.entries(filters)) {
          if (group.length > 0) {
            for (const [key, filter] of Object.entries(group)) {
              if (filter.id && (!filter.attributes.initializing || filter.attributes.initializing === false)) {
                parts.push(this.capitalizeText(filter.attributes.name))
              }
            }
          }
        }

        if (parts.length > 0) {
          if (parts.length > 3) {
            let extra = parts.length - 2
            parts = parts.slice(0, 2)
            name = parts.join(' + ') + ' + ' + extra + ' more'
          } else {
            name = parts.join(' + ')
          }

          if (name.length > 64) {
            name = name.substring(0, 61) + '...'
          }
        }
      }

      return name
    },
    close () {
      this.$emit('closed')
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.notification {
  @include font('content--md', $white, 0.9);
  position: absolute;
  top: 0px;
  width: 100%;
  height: auto;
  text-align: center;
  height: auto;
  background-color: $bg-3;
  border: 1px solid $black;

  @media screen and (min-width: $breakpointSm) {
    width: 448px;
  }

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg', $white, 0.9);
  }

  &__transition {
    &-enter-active, &-leave-active {
      transition: opacity 0.25s ease-out, transform 0.25s ease-out;
      transform: scale(1);
    }

    &-enter, &-leave-to {
      opacity: 0;
      transform: translateY(-20%) scale(0.6);
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    background-color: $bg-2;
    padding: 8px 12px 8px 16px;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    h5 {
      @include font('header--sm');
      text-transform: uppercase;
    }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    padding: 0.4rem 1rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;

    &--light {
      color: rgba($white, 0.7);
    }

    &--danger {
      color: $danger;
    }

    &--warning {
      color: $warning;
    }
  }

  &__info {
    margin: 0rem auto;
    padding: 0.75rem 1.5rem 1.25rem 1.5rem;
    border-top: 1px solid rgba($white, 0.1);
    border-bottom: 1px solid $black;
    color: rgba($white, 0.9);

    p {
      margin: 0;
      padding: 0;
    }
  }

  &__form {
    border-top: 1px solid rgba($white, 0.1);
  }

  &__tooltip {
    svg {
      position: relative;
      top: 3px;
      left: 3px;
      width: 18px;
      height: 18px;
      fill: rgba($white, 0.7);

      @media screen and (min-width: $breakpointLg) {
        width: 20px;
        height: 20px;
        top: 4px;
      }

      &:hover {
        fill: rgba($blue, 1);
      }
    }
  }
}

.row {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin: 0.75rem auto;
  padding: 0rem 1rem;

  @media screen and (min-width: $breakpointLg) {
    margin: 1rem auto;
  }

  &--border {
    border-bottom: 1px solid $black;
    margin: 0.75rem auto 0 auto !important;
    padding-bottom: 1rem !important;
  }

  &--separator {
    border-top: 1px solid rgba($white, 0.1);
    padding: calc(1rem - 1px) 1rem 0 1rem;
    margin: 0 !important;

    &Top {
      margin-top: 0.5rem;
    }
  }

  &__full {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;

    &--filters {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
    }

    &--condensed {
      justify-content: space-around;
    }
  }

  &__left {
    flex: 0.2;
    text-align: left;
  }

  &__right {
    flex: 0.8;
    text-align: left;
  }

  &__notice {
    color: rgba($white, 0.5);
    margin: 0 auto 1rem auto;
    display: block;
    font-style: italic;
  }

  &__center {
    margin: 0 auto;
    display: block;
  }

  &__error {
    color: $red;
    margin: 0;
  }

  &__label {
    color: rgba($white, 0.9);
    text-align: left;
  }

  &__operator {
    @include font('content--xs', $white, 0.5);
    position: relative;
    z-index: 9;
    font-weight: 500;
    margin: 0px 3px;
    text-transform: uppercase;
    display: inline-flex;

    /deep/ span {
      color: rgba($white, 0.7) !important;
    }

    &--active {
      color: rgba($white, 0.7);
    }
  }

  &__filter {
    position: relative;
    z-index: 10;
  }
}

.noOverflow {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

</style>
